'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import styles from './epx.module.css';

export interface ScreenshotItem { id: string; src: string; alt: string }
interface GalleryLabels { title: string; previous: string; next: string; close: string; positions: string[] }

export default function ScreenshotGallery({ items, labels, children }: { items: ScreenshotItem[]; labels: GalleryLabels; children: ReactNode }) {
  const [index, setIndex] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const active = index === null ? null : items[index];
  const move = (offset: number) => setIndex(current => current === null ? null : (current + offset + items.length) % items.length);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (index === null) { if (dialog.open) dialog.close(); return; }
    if (!dialog.open) dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [index]);

  function openImage(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
    const anchor = event.target.closest<HTMLButtonElement>('button[data-epx-screenshot]');
    if (!anchor) return;
    const selected = items.findIndex(item => item.id === anchor.dataset.epxScreenshot);
    if (selected < 0) return;
    event.preventDefault();
    triggerRef.current = anchor;
    setIndex(selected);
  }

  return <div role="presentation" onClick={openImage}>
    {children}
    <dialog ref={dialogRef} className={styles.gallery} aria-labelledby="epx-gallery-title" aria-describedby="epx-gallery-caption"
      onCancel={() => setIndex(null)} onClose={event => { if (!event.currentTarget.open) { setIndex(null); triggerRef.current?.focus(); } }}
      onClick={event => { if (event.target === event.currentTarget) setIndex(null); }}
      onKeyDown={event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
        if (event.key === 'Home') { event.preventDefault(); setIndex(0); }
        if (event.key === 'End') { event.preventDefault(); setIndex(items.length - 1); }
      }}>
      <div className={styles.galleryPanel}>
        <div className={styles.galleryHeader}><h2 id="epx-gallery-title">{labels.title}</h2><button type="button" aria-label={labels.close} onClick={() => setIndex(null)}><X size={24} aria-hidden="true" /></button></div>
        <div className={styles.galleryImage}
          onPointerDown={event => { if (event.isPrimary && event.button === 0) { touchStart.current = { x: event.clientX, y: event.clientY }; event.currentTarget.setPointerCapture(event.pointerId); } }}
          onPointerCancel={() => { touchStart.current = null; }}
          onPointerUp={event => {
            const start = touchStart.current; touchStart.current = null;
            if (!start) return;
            const dx = event.clientX - start.x, dy = event.clientY - start.y;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4) move(dx < 0 ? 1 : -1);
          }}>
          {active && <Image key={active.id} src={active.src} alt={active.alt} width={1280} height={720} sizes="(max-width: 768px) 100vw, 1280px" loading="eager" draggable={false} />}
        </div>
        <div className={styles.galleryFooter}>
          <button type="button" aria-label={labels.previous} onClick={() => move(-1)}><ArrowLeft size={22} aria-hidden="true" /></button>
          <div aria-live="polite" aria-atomic="true"><p id="epx-gallery-caption">{active?.alt}</p><span>{index === null ? '' : labels.positions[index]}</span></div>
          <button type="button" aria-label={labels.next} onClick={() => move(1)}><ArrowRight size={22} aria-hidden="true" /></button>
        </div>
      </div>
    </dialog>
  </div>;
}
